import { AlertTriangle, Inbox, LoaderCircle, LockKeyhole } from 'lucide-react'
import ArgusMark from './ArgusMark'

const STATE_CONFIG = {
  loading: {
    Icon: LoaderCircle,
    tone: 'cyan',
    frame: 'border-hd-cyan/25 bg-hd-cyan/8',
    accent: 'text-hd-cyan',
    glow: 'bg-hd-cyan/12',
  },
  empty: {
    Icon: Inbox,
    tone: 'blue',
    frame: 'border-hd-blue/25 bg-hd-blue/8',
    accent: 'text-hd-blue',
    glow: 'bg-hd-blue/12',
  },
  error: {
    Icon: AlertTriangle,
    tone: 'orange',
    frame: 'border-hd-orange/30 bg-hd-orange/8',
    accent: 'text-hd-orange',
    glow: 'bg-hd-orange/12',
  },
  locked: {
    Icon: LockKeyhole,
    tone: 'purple',
    frame: 'border-hd-purple/30 bg-hd-purple/8',
    accent: 'text-hd-purple',
    glow: 'bg-hd-purple/12',
  },
}

export default function ArgusStatePanel({
  variant = 'empty',
  eyebrow = 'ARGUS // STATUS',
  title,
  message,
  action = null,
  compact = false,
  fullScreen = false,
}) {
  const config = STATE_CONFIG[variant] ?? STATE_CONFIG.empty
  const Icon = config.Icon
  const content = (
    <section
      className={`argus-state-panel hd-card relative w-full overflow-hidden rounded-[2rem] border ${config.frame} ${
        compact ? 'p-4 sm:p-5' : 'p-6 sm:p-8'
      }`}
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'loading' ? 'polite' : undefined}
    >
      <div
        className={`pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full blur-3xl ${config.glow}`}
        aria-hidden="true"
      />
      <ArgusMark
        className={`pointer-events-none absolute -right-8 -bottom-14 h-40 w-40 opacity-[0.035] ${config.accent}`}
        title=""
      />

      <div className="relative flex items-start gap-4 sm:gap-5">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border sm:h-14 sm:w-14 ${config.frame} ${config.accent}`}
        >
          <Icon
            className={`h-5 w-5 sm:h-6 sm:w-6 ${variant === 'loading' ? 'animate-spin' : ''}`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[9px] font-black tracking-[0.2em] uppercase ${config.accent}`}>
            {eyebrow}
          </p>
          <h1
            className={`${compact ? 'mt-1 text-lg sm:text-xl' : 'mt-2 text-2xl sm:text-3xl'} font-black tracking-[-0.035em]`}
          >
            {title}
          </h1>
          {message && (
            <p className="text-hd-text-secondary mt-2 max-w-2xl text-xs leading-relaxed sm:text-sm">
              {message}
            </p>
          )}
          {action && <div className="mt-5 flex flex-wrap gap-2">{action}</div>}
        </div>
      </div>
    </section>
  )

  if (!fullScreen) return content

  return (
    <main className="argus-state-screen bg-hd-dark text-hd-text-primary relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-8 sm:px-6">
      <div className="bg-hd-blue/8 pointer-events-none absolute -top-32 -left-24 h-80 w-80 rounded-full blur-3xl" />
      <div className="bg-hd-purple/8 pointer-events-none absolute -right-24 -bottom-32 h-80 w-80 rounded-full blur-3xl" />
      <div className="relative w-full max-w-2xl">{content}</div>
    </main>
  )
}
